/**
 * Combined date and time utility functions
 * Provides comprehensive date/time operations, formatting, validation, and calculations
 */

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday';

/**
 * Date and time helper utilities
 * Note: All times are stored in UTC but displayed in Netherlands timezone
 */

// Netherlands timezone constants
export const NL_LOCALE = 'nl-NL';
export const NL_TIMEZONE = 'Europe/Amsterdam';

/**
 * Date and time helper utilities
 */
export class DateHelpers {
  /**
   * Convert a date string (YYYY-MM-DD) to day of week
   * @param dateString - Date in YYYY-MM-DD format
   * @returns Day name or null if invalid
   */
  static getDayNameFromDate(dateString: string): DayOfWeek | null {
    try {
      const date = new Date(dateString);
      const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

      const dayMapping: { [key: number]: DayOfWeek } = {
        1: 'monday',
        2: 'tuesday',
        3: 'wednesday',
        4: 'thursday',
        5: 'friday',
      };

      return dayMapping[dayIndex] || null; // Only weekdays
    } catch (_error) {
      console.warn('Invalid date format:', dateString);
      return null;
    }
  }

  /**
   * Check if a date string is a valid weekday (Monday-Friday)
   * @param dateString - Date in YYYY-MM-DD format
   * @returns true if it's a weekday
   */
  static isWeekday(dateString: string): boolean {
    return this.getDayNameFromDate(dateString) !== null;
  }

  /**
   * Format date for display in Netherlands locale
   * @param dateString - Date in YYYY-MM-DD format
   * @returns Formatted date string in Netherlands locale
   */
  static formatDateForDisplay(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(NL_LOCALE, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (_error) {
      return dateString; // Return original if formatting fails
    }
  }

  /**
   * Format time for display (24h to 12h format)
   * @param timeString - Time in HH:MM format
   * @returns Formatted time string
   */
  static formatTimeForDisplay(timeString: string): string {
    try {
      const timeParts = timeString.split(':');
      if (timeParts.length !== 2) return timeString;

      const [hours, minutes] = timeParts;
      if (!hours || !minutes) return timeString;

      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
      return `${hour12}:${minutes} ${ampm}`;
    } catch (_error) {
      return timeString; // Return original if formatting fails
    }
  }

  /**
   * Format time string (e.g., "10:00:00") into a HH:MM format.
   * @param timeString The time string to format.
   * @returns Formatted time string in HH:MM format
   */
  static formatDisplayTime(timeString: string): string {
    if (!timeString) {
      return '';
    }

    if (timeString.length < 5) {
      return timeString; // Return original string as fallback
    }

    return timeString.substring(0, 5);
  }

  /**
   * Formats a date string (e.g., "2025-06-23") into a DD/MM/YYYY format for Netherlands
   * @param dateString The date string to format.
   * @param locale The locale to use for formatting (defaults to nl-NL)
   * @returns Formatted date string in DD/MM/YYYY format
   */
  static formatDisplayDate(dateString: string, locale = 'nl-NL'): string {
    if (!dateString) {
      return '';
    }

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original string as fallback
    }

    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
      .format(date)
      .replace(/-/g, '/');
  }

  /**
   * Create a time range string for display
   * @param timeFrom - Start time in HH:MM format
   * @param timeTill - End time in HH:MM format
   * @returns Formatted time range string
   */
  static formatTimeRange(timeFrom: string, timeTill: string): string {
    const startTime = this.formatTimeForDisplay(timeFrom);
    const endTime = this.formatTimeForDisplay(timeTill);
    return `${startTime} - ${endTime}`;
  }

  /**
   * Validate date format (YYYY-MM-DD)
   * @param dateString - Date string to validate
   * @returns true if valid format
   */
  static isValidDateFormat(dateString: string): boolean {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) {
      return false;
    }

    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  }

  /**
   * Validate time format (HH:MM)
   * @param timeString - Time string to validate
   * @returns true if valid format
   */
  static isValidTimeFormat(timeString: string): boolean {
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(timeString);
  }

  /**
   * Check if a shift date is in the future (Netherlands timezone)
   * @param dateString - Date in YYYY-MM-DD format
   * @returns true if date is in the future or today
   */
  static isFutureDate(dateString: string): boolean {
    try {
      // Get today's date in Netherlands timezone
      const today = new Date();
      const nlDateString = today.toLocaleDateString('en-CA', {
        timeZone: NL_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });

      // Compare the date strings directly (YYYY-MM-DD format)
      return dateString >= nlDateString;
    } catch (_error) {
      return false;
    }
  }

  /**
   * Get today's date in YYYY-MM-DD format (Netherlands timezone)
   * @returns Today's date string
   */
  static getTodayString(): string {
    const datePart = new Date().toISOString().split('T')[0];
    return datePart || '';
  }

  /**
   * Add days to a date string
   * @param dateString - Date in YYYY-MM-DD format
   * @param days - Number of days to add
   * @returns New date string
   */
  static addDays(dateString: string, days: number): string {
    try {
      const date = new Date(dateString);
      date.setDate(date.getDate() + days);
      const datePart = date.toISOString().split('T')[0];
      return datePart || dateString;
    } catch (_error) {
      return dateString;
    }
  }
}

// Export standalone functions for backward compatibility
export const formatDisplayDate = DateHelpers.formatDisplayDate;
export const formatDisplayTime = DateHelpers.formatDisplayTime;
export const getDayNameFromDate = DateHelpers.getDayNameFromDate;
export const isWeekday = DateHelpers.isWeekday;
export const formatDateForDisplay = DateHelpers.formatDateForDisplay;
export const formatTimeForDisplay = DateHelpers.formatTimeForDisplay;
export const formatTimeRange = DateHelpers.formatTimeRange;
export const isValidDateFormat = DateHelpers.isValidDateFormat;
export const isValidTimeFormat = DateHelpers.isValidTimeFormat;
export const isFutureDate = DateHelpers.isFutureDate;
export const getTodayString = DateHelpers.getTodayString;
export const addDays = DateHelpers.addDays;

/**
 * Check if a shift time has passed (expired) - UTC comparison for Netherlands timezone
 * @param shiftDate - Date in YYYY-MM-DD format
 * @param shiftTime - Time in HH:MM:SS format
 * @returns boolean - true if shift time has passed
 */
export function isShiftTimeExpired(shiftDate: string, shiftTime: string): boolean {
  const now = new Date();
  const shiftDateTime = new Date(`${shiftDate}T${shiftTime}`);

  return shiftDateTime < now;
}

/**
 * Check if a shift is still active (hasn't ended yet) - Netherlands timezone
 * @param shiftDate - Date in YYYY-MM-DD format
 * @param timeFrom - Start time in HH:MM:SS format
 * @param timeTill - End time in HH:MM:SS format
 * @returns boolean - true if shift is still active
 */
export function isShiftActive(shiftDate: string, timeFrom: string, timeTill: string): boolean {
  const now = new Date();
  const shiftStartDateTime = new Date(`${shiftDate}T${timeFrom}`);
  const shiftEndDateTime = new Date(`${shiftDate}T${timeTill}`);

  // Shift is active if it hasn't started yet OR if it has started but hasn't ended
  return shiftStartDateTime > now || (shiftStartDateTime <= now && shiftEndDateTime > now);
}

/**
 * Check if a shift can still be applied to (hasn't started yet) - Netherlands timezone
 * @param shiftDate - Date in YYYY-MM-DD format
 * @param timeFrom - Start time in HH:MM:SS format
 * @returns boolean - true if shift can still be applied to
 */
export function canApplyToShift(shiftDate: string, timeFrom: string): boolean {
  const now = new Date();
  const shiftStartDateTime = new Date(`${shiftDate}T${timeFrom}`);

  return shiftStartDateTime > now;
}

/**
 * Get current time string in HH:MM:SS format (UTC)
 * @returns string - Current time in HH:MM:SS format
 */
export function getCurrentTimeString(): string {
  const now = new Date();
  return now.toISOString().slice(11, 19); // HH:MM:SS in UTC
}

/**
 * Get current date and time as ISO string (UTC)
 * @returns string - Current date and time in ISO format
 */
export function getCurrentDateTime(): string {
  return new Date().toISOString();
}

/**
 * Parse time string into hours and minutes
 * @param timeString - Time in HH:MM format
 * @returns Object with hours and minutes or null if invalid
 */
function parseTime(timeString: string): { hours: number; minutes: number } | null {
  if (!timeString || !timeString.includes(':')) return null;

  const [hoursStr, minutesStr] = timeString.split(':');
  const hours = parseInt(hoursStr || '0');
  const minutes = parseInt(minutesStr || '0');

  if (isNaN(hours) || isNaN(minutes)) return null;
  return { hours, minutes };
}

/**
 * Convert time string to total minutes for easy comparison
 * @param timeString - Time in HH:MM format
 * @returns Total minutes or null if invalid
 */
function timeToMinutes(timeString: string): number | null {
  const time = parseTime(timeString);
  if (!time) return null;
  return time.hours * 60 + time.minutes;
}

/**
 * Check if a shift date and time combination is in the past (Netherlands timezone)
 * @param dateString - Date in YYYY-MM-DD format
 * @param timeString - Time in HH:MM format
 * @returns true if the date/time combination is in the past
 */
export function isShiftDateTimeInPast(dateString: string, timeString: string): boolean {
  try {
    // Create shift datetime - assume local timezone (which should be Netherlands for your server)
    const shiftDateTime = new Date(`${dateString}T${timeString}:00`);

    // Get current time in Netherlands timezone
    const now = new Date();
    const nlTimeString = now.toLocaleString('en-CA', {
      timeZone: NL_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    // Convert to Date object for comparison
    const nlTime = new Date(nlTimeString);

    return shiftDateTime <= nlTime;
  } catch (_error) {
    return true; // Consider date/time in past if parsing fails
  }
}

/**
 * Validate that end time is after start time
 * @param timeFrom - Start time in HH:MM format
 * @param timeTill - End time in HH:MM format
 * @returns true if end time is after start time
 */
export function isValidTimeRange(timeFrom: string, timeTill: string): boolean {
  const fromMinutes = timeToMinutes(timeFrom);
  const tillMinutes = timeToMinutes(timeTill);

  if (fromMinutes === null || tillMinutes === null) return false;
  return tillMinutes > fromMinutes;
}
